import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Copy, ExternalLink, AlertTriangle, CheckCircle2, TrendingUp, TrendingDown, Server, Info } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, ReferenceLine } from "recharts";

// Prop firms preset labels
const PROP_FIRMS = [
  { id: "ftmo",         label: "FTMO",                logo: "🇨🇿" },
  { id: "fundednext",   label: "FundedNext",          logo: "🌐" },
  { id: "the5ers",      label: "The5%ers",            logo: "5️⃣" },
  { id: "myforexfunds", label: "MyForexFunds",        logo: "💱" },
  { id: "topstep",      label: "TopStep (cTrader)",   logo: "🎯" },
  { id: "e8",           label: "E8 Funding",          logo: "8️⃣" },
  { id: "ftuk",         label: "FTUK",                logo: "🇬🇧" },
  { id: "fundingpips",  label: "FundingPips",         logo: "💧" },
  { id: "custom",       label: "Autre / Personnalisé", logo: "⚙️" },
];

const BROKERS = ["MT4", "MT5", "cTrader"];

export default function PropFirmAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createdToken, setCreatedToken] = useState(null); // { id, api_token, name, broker }
  const [detail, setDetail] = useState(null); // detailed status for selected account

  const authHeader = () => {
    const t = localStorage.getItem("auth_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/propfirm/accounts`, { headers: authHeader() });
      setAccounts(r.data.items || []);
    } catch (e) {
      console.warn("propfirm/accounts load failed:", e?.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadAccounts();
    axios.get(`${API}/propfirm/presets`).then(r => setPresets(r.data.items || [])).catch(() => {});
  }, [loadAccounts]);

  const loadDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/propfirm/accounts/${id}`, { headers: authHeader() });
      setDetail(r.data);
    } catch { toast.error("Erreur chargement détail"); }
  };

  const deleteAccount = async (id, name) => {
    if (!window.confirm(`Supprimer le compte "${name}" ? Toutes les données seront effacées.`)) return;
    try {
      await axios.delete(`${API}/propfirm/accounts/${id}`, { headers: authHeader() });
      toast.success("Compte supprimé");
      if (detail?.account?.id === id) setDetail(null);
      loadAccounts();
    } catch { toast.error("Suppression échouée"); }
  };

  return (
    <div className="p-6" data-testid="propfirm-live-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="propfirm-live-title">
              Mes Comptes Prop Firm
            </h1>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-accent-green/15 text-accent-green border border-accent-green/30">
              Live via Bridge EA
            </span>
          </div>
          <p className="text-white/60 text-sm max-w-3xl">
            Connectez vos comptes réels <strong className="text-white">FTMO, FundedNext, The5%ers, TopStep</strong> (et autres)
            à ANATRADEX. Suivez balance, équité, drawdown, objectif de profit et conformité aux règles en temps réel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-secondary" onClick={loadAccounts} data-testid="propfirm-refresh">
            <RefreshCw size={14} /> Actualiser
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="propfirm-new-account">
            <Plus size={14} /> Ajouter un compte
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!loading && accounts.length === 0 && (
        <div className="panel p-10 text-center" data-testid="propfirm-empty">
          <div className="inline-flex w-14 h-14 rounded-full bg-accent-blue/15 border border-accent-blue/30 items-center justify-center mb-4">
            <Server size={24} className="text-accent-blue" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">Aucun compte connecté</h3>
          <p className="text-sm text-white/60 max-w-md mx-auto mb-4">
            Ajoutez votre premier compte pour synchroniser en temps réel votre challenge FTMO, FundedNext, The5%ers ou tout autre prop firm.
          </p>
          <button className="btn btn-primary mx-auto" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Créer mon premier compte
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {accounts.map((a) => (
          <AccountCard key={a.id} account={a} onOpen={() => loadDetail(a.id)} onDelete={() => deleteAccount(a.id, a.name)} />
        ))}
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateAccountModal
          presets={presets}
          onClose={() => setShowCreate(false)}
          onCreated={(payload) => {
            setShowCreate(false);
            setCreatedToken(payload);
            loadAccounts();
          }}
        />
      )}
      {createdToken && (
        <TokenModal payload={createdToken} onClose={() => setCreatedToken(null)} />
      )}
      {detail && (
        <AccountDetailModal detail={detail} onClose={() => setDetail(null)} onRefresh={() => loadDetail(detail.account.id)} />
      )}
    </div>
  );
}

// ============ Card ============
function AccountCard({ account, onOpen, onDelete }) {
  const snap = account.last_snapshot;
  const equity = snap?.equity ?? account.initial_balance;
  const pnl = equity - account.initial_balance;
  const pnlPct = (pnl / account.initial_balance * 100);
  const positive = pnl >= 0;
  const online = !!account.last_seen_at &&
                 (new Date().getTime() - new Date(account.last_seen_at).getTime()) < 5 * 60 * 1000;

  return (
    <div className="panel p-4 flex flex-col hover:border-accent-blue/40 transition" data-testid={`propfirm-card-${account.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg leading-none">{PROP_FIRMS.find(p => p.id === account.prop_firm)?.logo || "🏦"}</span>
            <span className="font-heading font-semibold">{account.name}</span>
          </div>
          <div className="text-[10px] text-white/40 flex items-center gap-2">
            <span>{account.broker}</span>
            <span>·</span>
            <span>{account.currency}</span>
            <span>·</span>
            <span className={online ? "text-accent-green" : "text-white/30"}>
              {online ? "● Connecté" : "○ Hors ligne"}
            </span>
          </div>
        </div>
        <button onClick={onDelete} className="text-white/30 hover:text-red-400 transition p-1"
                data-testid={`propfirm-delete-${account.id}`}>
          <Trash2 size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <MetricCell label="Balance" value={`${(snap?.balance ?? account.initial_balance).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${account.currency}`} />
        <MetricCell label="Équité"  value={`${equity.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${account.currency}`} />
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-white/50">P&L total</span>
        <span className={`text-sm font-mono font-semibold flex items-center gap-1 ${positive ? "text-accent-green" : "text-red-400"}`}>
          {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {positive ? "+" : ""}{pnl.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} {account.currency} ({pnlPct.toFixed(2)}%)
        </span>
      </div>

      <button className="btn btn-secondary w-full justify-center mt-auto" onClick={onOpen}
              data-testid={`propfirm-open-${account.id}`}>
        <ExternalLink size={13} /> Détail & Conformité
      </button>
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="bg-white/5 rounded-md px-3 py-2 border border-white/5">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="font-mono text-sm mt-1">{value}</div>
    </div>
  );
}

// ============ Create Modal ============
function CreateAccountModal({ onClose, onCreated, presets = [] }) {
  const [form, setForm] = useState({
    name: "",
    broker: "MT4",
    prop_firm: "ftmo",
    initial_balance: 100000,
    currency: "USD",
    preset: "custom",
  });
  const [submitting, setSubmitting] = useState(false);

  const applyPreset = (presetId) => {
    const p = presets.find(x => x.id === presetId);
    if (!p) { setForm(f => ({ ...f, preset: presetId })); return; }
    setForm(f => ({
      ...f,
      preset: p.id,
      prop_firm: p.prop_firm || f.prop_firm,
      initial_balance: p.initial_balance || f.initial_balance,
      name: f.name || p.label, // pre-fill name if empty
    }));
    toast.success(`Preset "${p.label}" appliqué`);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await axios.post(`${API}/propfirm/accounts`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      onCreated(r.data);
    } catch (e) {
      toast.error("Création échouée : " + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const selectedPreset = presets.find(p => p.id === form.preset);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="propfirm-create-modal">
      <div className="panel max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-lg">Nouveau compte prop firm</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">×</button>
        </div>

        <div className="space-y-4">
          {/* Presets 1-clic */}
          {presets.length > 0 && (
            <FormField label="Preset règles (1 clic)" hint="Charge les règles officielles de la prop firm sélectionnée">
              <select className="input w-full" value={form.preset}
                      onChange={(e) => applyPreset(e.target.value)}
                      data-testid="propfirm-form-preset">
                {presets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              {selectedPreset && selectedPreset.id !== "custom" && (
                <div className="text-[10px] text-white/50 bg-white/5 border border-white/10 rounded p-2 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  <span>Max DD : <b className="text-white">{selectedPreset.max_total_loss_pct}%</b></span>
                  <span>Daily DD : <b className="text-white">{selectedPreset.max_daily_loss_pct}%</b></span>
                  <span>Objectif : <b className="text-accent-green">{selectedPreset.profit_target_pct}%</b></span>
                  <span>Jours min : <b className="text-white">{selectedPreset.min_trading_days}</b></span>
                </div>
              )}
            </FormField>
          )}

          <FormField label="Nom du compte" hint="ex: FTMO Challenge 100k">
            <input type="text" className="input w-full" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                   placeholder="FTMO Challenge 100k" data-testid="propfirm-form-name" />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Plateforme">
              <select className="input w-full" value={form.broker} onChange={(e) => setForm({...form, broker: e.target.value})}
                      data-testid="propfirm-form-broker">
                {BROKERS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </FormField>
            <FormField label="Prop Firm">
              <select className="input w-full" value={form.prop_firm} onChange={(e) => setForm({...form, prop_firm: e.target.value})}
                      data-testid="propfirm-form-firm">
                {PROP_FIRMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Capital initial">
              <input type="number" className="input w-full" value={form.initial_balance}
                     onChange={(e) => setForm({...form, initial_balance: parseFloat(e.target.value) || 0})}
                     data-testid="propfirm-form-balance" />
            </FormField>
            <FormField label="Devise">
              <select className="input w-full" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})}>
                {["USD","EUR","GBP","CHF","AUD","CAD"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
          </div>

          <div className="text-xs text-white/50 bg-white/5 border border-white/10 rounded-md p-3 flex gap-2">
            <Info size={14} className="text-accent-blue shrink-0 mt-0.5" />
            <div>
              Après création, un <strong className="text-white">token unique</strong> vous sera fourni.
              Copiez-le dans les propriétés du ANATRADEX Bridge EA sur votre plateforme, et vos données arriveront ici en temps réel.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}
                  data-testid="propfirm-form-submit">
            {submitting ? "Création..." : "Créer et obtenir le token"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-white/60 mb-1 block">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

// ============ Token Display Modal (shown once, right after create) ============
function TokenModal({ payload, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(payload.api_token); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const bridgeDownloadUrl = payload.broker === "MT5" ? `${API}/downloads/bridge_mt5`
                          : payload.broker === "cTrader" ? `${API}/downloads/bridge_ctrader`
                          : `${API}/downloads/bridge_mt4`;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="propfirm-token-modal">
      <div className="panel max-w-xl w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-accent-green/15 border border-accent-green/30 grid place-items-center">
            <CheckCircle2 size={20} className="text-accent-green" />
          </div>
          <div>
            <div className="font-heading font-semibold text-lg">Compte créé — voici votre token</div>
            <div className="text-xs text-white/50">Ce token ne sera plus jamais réaffiché. Copiez-le maintenant.</div>
          </div>
        </div>

        <div className="bg-black/50 border border-accent-blue/30 rounded-md p-4 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Votre API Token</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm text-accent-blue flex-1 break-all" data-testid="propfirm-token-value">
              {payload.api_token}
            </code>
            <button className="btn btn-primary" onClick={copy} data-testid="propfirm-token-copy">
              <Copy size={12} /> {copied ? "Copié !" : "Copier"}
            </button>
          </div>
        </div>

        <div className="space-y-3 text-sm text-white/70 mb-6">
          <div className="font-semibold text-white">Prochaines étapes :</div>
          <ol className="space-y-2 list-decimal list-inside ml-1">
            <li>
              Téléchargez le <strong>ANATRADEX Bridge EA</strong> pour <strong>{payload.broker}</strong> :{" "}
              <a href={bridgeDownloadUrl} className="text-accent-blue hover:underline" download>
                Télécharger le Bridge
              </a>
            </li>
            <li>Placez-le dans le dossier <code className="font-mono text-xs bg-white/5 px-1 rounded">Experts/</code> de votre plateforme et redémarrez.</li>
            <li>Glissez le Bridge sur n'importe quel graphique de votre compte {payload.broker}.</li>
            <li>Dans ses propriétés, collez ce token dans le champ <code className="font-mono text-xs bg-white/5 px-1 rounded">ApiToken</code>.</li>
            <li>
              <strong className="text-accent-blue">Important</strong> : autorisez l'URL{" "}
              <code className="font-mono text-xs bg-white/5 px-1 rounded">https://anatradex.app</code> dans
              Outils → Options → Expert Advisors → WebRequest.
            </li>
          </ol>
        </div>

        <button className="btn btn-primary w-full justify-center" onClick={onClose}>J'ai copié le token, continuer</button>
      </div>
    </div>
  );
}

// ============ Detail Modal ============
function AccountDetailModal({ detail, onClose, onRefresh }) {
  const { account, trades_count, status } = detail;
  const [trades, setTrades] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [journal, setJournal] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [rTrades, rSnaps, rJournal] = await Promise.all([
          axios.get(`${API}/propfirm/accounts/${account.id}/trades`, { headers }),
          axios.get(`${API}/propfirm/accounts/${account.id}/snapshots?hours=168`, { headers }),
          axios.get(`${API}/propfirm/accounts/${account.id}/journal`, { headers }),
        ]);
        setTrades(rTrades.data.items || []);
        setSnapshots(rSnaps.data.items || []);
        setJournal(rJournal.data || null);
      } catch {}
    };
    load();
  }, [account.id]);

  const initialBalance = account.initial_balance;
  const maxTotalLoss  = status.max_total_loss_dollar; // negative
  const profitTarget  = status.profit_target_dollar;  // positive

  // Format snapshots for chart
  const chartData = snapshots.map(s => ({
    at: new Date(s.at).getTime(),
    label: new Date(s.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
    equity: s.equity,
    balance: s.balance,
  }));

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="propfirm-detail-modal">
      <div className="panel max-w-4xl w-full p-6 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-heading font-semibold text-lg">{account.name}</div>
            <div className="text-xs text-white/50">
              {account.broker} · {account.prop_firm} · {account.currency} · Créé le{" "}
              {new Date(account.created_at).toLocaleDateString("fr-FR")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={onRefresh}>
              <RefreshCw size={13} /> Actualiser
            </button>
            <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
          </div>
        </div>

        {/* Compliance Bars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ProgressBar
            label="Objectif de profit"
            pct={status.target_progress_pct}
            hint={`${status.total_pnl.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} / ${status.profit_target_dollar.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${account.currency}`}
            color="green"
          />
          <ProgressBar
            label="Max Daily Loss utilisé"
            pct={status.daily_used_pct}
            hint={`Perte quotidienne : ${status.daily_pnl.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${account.currency}`}
            color={status.daily_used_pct >= 80 ? "red" : status.daily_used_pct >= 50 ? "orange" : "blue"}
          />
          <ProgressBar
            label="Max Total Loss utilisé"
            pct={status.total_used_pct}
            hint={`Perte totale : ${status.total_pnl.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${account.currency}`}
            color={status.total_used_pct >= 80 ? "red" : status.total_used_pct >= 50 ? "orange" : "blue"}
          />
        </div>

        {/* Alerts */}
        {status.alerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {status.alerts.map((a, i) => (
              <div key={i} className={`p-3 rounded-md border text-sm flex items-start gap-2 ${
                a.level === "danger" ? "bg-red-500/10 border-red-500/30 text-red-300" :
                a.level === "warning" ? "bg-orange-500/10 border-orange-500/30 text-orange-300" :
                "bg-green-500/10 border-green-500/30 text-green-300"
              }`}>
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <div>{a.msg}</div>
              </div>
            ))}
          </div>
        )}

        {/* Days */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <MetricCell label="Jours tradés"       value={`${status.trading_days_used} / ${status.trading_days_required} min`} />
          <MetricCell label="Trades enregistrés" value={trades_count} />
        </div>

        {/* Equity Curve */}
        <div className="panel-title text-[13px] mb-2 flex items-center gap-2">
          Courbe d'équité (7 derniers jours)
          <span className="text-[10px] font-normal text-white/40">{snapshots.length} snapshots</span>
        </div>
        <div className="bg-black/30 border border-white/5 rounded-md p-3 mb-6" data-testid="propfirm-equity-chart">
          {chartData.length < 2 ? (
            <div className="h-40 flex items-center justify-center text-xs text-white/40">
              Pas encore assez de données. Le Bridge EA doit tourner sur votre plateforme pour alimenter la courbe.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fill: "#ffffff40", fontSize: 10 }} tickLine={false} axisLine={false}
                       interval={Math.max(0, Math.floor(chartData.length / 6))} />
                <YAxis tick={{ fill: "#ffffff40", fontSize: 10 }} tickLine={false} axisLine={false}
                       domain={["auto", "auto"]}
                       tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
                <RTooltip
                  contentStyle={{ background: "#111827", border: "1px solid #333", borderRadius: 6, fontSize: 11 }}
                  labelStyle={{ color: "#9ca3af" }}
                  formatter={(v, k) => [`${v.toLocaleString("fr-FR")} ${account.currency}`, k === "equity" ? "Équité" : "Balance"]}
                />
                <ReferenceLine y={initialBalance} stroke="#ffffff40" strokeDasharray="3 3"
                               label={{ value: "Départ", fill: "#ffffff60", fontSize: 9, position: "insideTopLeft" }} />
                <ReferenceLine y={initialBalance + profitTarget} stroke="#4ade80" strokeDasharray="3 3"
                               label={{ value: "Cible +profit", fill: "#4ade80", fontSize: 9, position: "insideTopRight" }} />
                <ReferenceLine y={initialBalance + maxTotalLoss} stroke="#ef4444" strokeDasharray="3 3"
                               label={{ value: "Max Loss", fill: "#ef4444", fontSize: 9, position: "insideBottomRight" }} />
                <Area type="monotone" dataKey="equity" stroke="#3b82f6" strokeWidth={2}
                      fill="url(#equityGradient)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Trades */}
        <div className="panel-title text-[13px] mb-3">Derniers trades ({trades.length})</div>
        <div className="max-h-64 overflow-y-auto border border-white/5 rounded-md">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-black/60 text-white/50">
              <tr>
                <th className="px-3 py-2 text-left">Symbole</th>
                <th className="px-3 py-2 text-left">Side</th>
                <th className="px-3 py-2 text-right">Vol</th>
                <th className="px-3 py-2 text-right">P&L</th>
                <th className="px-3 py-2 text-right">Fermé</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-6 text-white/40">
                  Aucun trade enregistré. Le Bridge EA doit tourner sur votre plateforme.
                </td></tr>
              ) : trades.map(t => (
                <tr key={t.ticket} className="border-t border-white/5">
                  <td className="px-3 py-1.5 font-mono">{t.symbol}</td>
                  <td className="px-3 py-1.5">
                    <span className={t.side === "BUY" ? "text-accent-green" : "text-red-400"}>{t.side}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{t.volume}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${t.pnl >= 0 ? "text-accent-green" : "text-red-400"}`}>
                    {t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(2)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-white/50">{(t.close_time || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Journal de trading (psycho) */}
        {journal && journal.total_trades > 0 && (
          <div className="mt-6" data-testid="propfirm-journal-section">
            <div className="panel-title text-[13px] mb-3 flex items-center gap-2">
              🧠 Journal psychologique
              <span className="text-[10px] font-normal text-white/40">
                {journal.summary.total_trades} trades · Win rate {journal.summary.win_rate_pct}%
              </span>
            </div>

            {/* Insights */}
            {journal.summary.insights.length > 0 && (
              <div className="space-y-2 mb-4">
                {journal.summary.insights.map((ins, i) => (
                  <div key={i} className={`p-3 rounded-md border text-sm flex items-start gap-2 ${
                    ins.level === "danger"  ? "bg-red-500/10 border-red-500/30 text-red-300" :
                    ins.level === "warning" ? "bg-orange-500/10 border-orange-500/30 text-orange-300" :
                    ins.level === "good"    ? "bg-green-500/10 border-green-500/30 text-green-300" :
                    "bg-white/5 border-white/10 text-white/70"
                  }`} data-testid={`journal-insight-${i}`}>
                    <span>{ins.msg}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tag counts */}
            {Object.keys(journal.summary.tag_counts).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="journal-tag-cloud">
                {Object.entries(journal.summary.tag_counts).map(([tag, count]) => (
                  <span key={tag} className={`text-[11px] px-2 py-1 rounded-full border ${
                    tag === "revenge" || tag === "overtrading" ? "bg-red-500/10 border-red-500/30 text-red-300" :
                    tag === "impulsive" ? "bg-orange-500/10 border-orange-500/30 text-orange-300" :
                    tag === "patience" ? "bg-green-500/10 border-green-500/30 text-green-300" :
                    "bg-white/5 border-white/10 text-white/60"
                  }`}>
                    {tag} × {count}
                  </span>
                ))}
              </div>
            )}

            {/* Recent tagged trades */}
            <div className="max-h-40 overflow-y-auto border border-white/5 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 p-2 bg-black/40">Trades récents avec tags</div>
              {journal.trades.filter(t => t.tags && t.tags.length > 0).slice(-10).reverse().map(t => (
                <div key={t.ticket} className="border-t border-white/5 p-2 text-xs flex items-start gap-2">
                  <div className="font-mono text-white/50 w-14 shrink-0">{t.symbol}</div>
                  <div className={`w-14 shrink-0 font-mono ${t.pnl >= 0 ? "text-accent-green" : "text-red-400"}`}>
                    {t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(0)}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {t.tags.map((tg, i) => (
                      <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${
                        tg.level === "danger"  ? "bg-red-500/20 text-red-300" :
                        tg.level === "warning" ? "bg-orange-500/20 text-orange-300" :
                        tg.level === "good"    ? "bg-green-500/20 text-green-300" :
                        "bg-white/10 text-white/60"
                      }`} title={tg.hint}>
                        {tg.label}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {journal.trades.filter(t => t.tags && t.tags.length > 0).length === 0 && (
                <div className="text-center py-4 text-[11px] text-white/40">
                  Aucun schéma psychologique détecté sur vos derniers trades. Continuez !
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ label, pct, hint, color = "blue" }) {
  const colorMap = {
    blue: "bg-accent-blue",
    green: "bg-accent-green",
    orange: "bg-orange-500",
    red: "bg-red-500",
  };
  const clamped = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className="bg-white/5 rounded-md p-3 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-white/50">{label}</div>
        <div className="font-mono text-xs font-semibold">{clamped.toFixed(1)}%</div>
      </div>
      <div className="h-2 bg-black/40 rounded-full overflow-hidden mb-2">
        <div className={`h-full ${colorMap[color]} transition-all`} style={{ width: `${clamped}%` }} />
      </div>
      <div className="text-[10px] text-white/40">{hint}</div>
    </div>
  );
}
